import type { ReactNode } from 'react';

import { Box, type SxProps, type Theme, useTheme } from '@mui/material';

/**
 * The outlined-input frame for rich-text editors: same border, hover and focus
 * behaviour as `MuiOutlinedInput`, so an editor reads like any other field.
 * The background is painted by the editor itself (`qlEditorSurfaceStyles`).
 */
export const RichEditorOutline = ({
  children,
  error = false,
  disabled = false,
  sx,
}: {
  children: ReactNode;
  error?: boolean;
  // A disabled editor must not react to hover/focus, like a disabled input.
  disabled?: boolean;
  // The frame sits between the host and the editor, so a host that sizes the
  // editor (percentage heights) must be able to pass its height through.
  sx?: SxProps<Theme>;
}) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;

  // Read the resting border color off the theme's own `MuiOutlinedInput`
  // override instead of re-importing the palette constant that override was
  // built from. A copy would look identical today and desync silently the day
  // the override moves; this follows it.
  const notchedOutline = theme.components?.MuiOutlinedInput?.styleOverrides
    ?.notchedOutline as { borderColor?: string } | undefined;
  const restingBorderColor = notchedOutline?.borderColor ?? palette.divider;

  return (
    <Box
      sx={[
        {
          position: 'relative',
          // Confine the frame's z-index to this editor. Without it the
          // fieldset below (zIndex 2) resolves against whatever stacking
          // context the host sits in and paints over sibling overlays that
          // legitimately sit above the editor - a dropdown anchored just
          // above it (`PopperMenu`, zIndex 1) came out with the editor's
          // border drawn across it.
          isolation: 'isolate',
          // Hover must not apply in the error state: `&:hover > fieldset`
          // out-specifies the error rule (0,2,1 vs 0,1,1), so an unguarded
          // hover would repaint an errored frame back to text.primary.
          ...(!disabled &&
            !error && {
              '&:hover > fieldset': { borderColor: palette.text.primary },
            }),
          ...(!disabled && {
            '&:focus-within > fieldset': {
              borderColor: palette.primary.main,
              borderWidth: 2,
            },
          }),
          ...(error && {
            '& > fieldset, &:hover > fieldset, &:focus-within > fieldset': {
              borderColor: palette.error.main,
            },
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
      <Box
        // Decorative only: a fieldset with no legend and no controls inside is
        // still announced as a group, so hide it like MUI's NotchedOutline does.
        aria-hidden
        component="fieldset"
        sx={{
          position: 'absolute',
          inset: 0,
          m: 0,
          px: 1,
          pointerEvents: 'none',
          // Above any sticky editor toolbar (zIndex 1), so the frame (and
          // its focus ring) wraps the whole control instead of being
          // painted over in the toolbar zone.
          zIndex: 2,
          borderRadius: 2,
          border: `1px solid ${restingBorderColor}`,
          transition: 'all 0.15s ease',
          overflow: 'hidden',
        }}
      />
    </Box>
  );
};
